/*
  Order limits.

  Caps how many units one visitor can push through checkout, set in
  Theme settings > SajaaDo > Order limits. Cash on delivery is what makes this
  worth having: a joke order of 9999 units books like any other order and costs
  real money to pack or chase.

  Liquid does the first pass. It puts `max` on every quantity box, renders the
  notice and kills the checkout button on page load. This file keeps all three
  honest afterwards, because the cart updates over AJAX and the cart page only
  swaps its subtotal back in, never the buttons.

  Three layers, cheapest first:
    1. `max` on the quantity inputs, so the plus button stops and a typed number
       is rejected by the theme's own cart validation.
    2. This file, watching every cart update, greying the checkout button and
       naming the cap that was hit.
    3. A submit handler on the cart forms, the last thing standing if someone
       arrives over the cap from a /cart/{variant}:{quantity} permalink.

  None of it is a lock. /checkout is reachable by hand whatever the storefront
  says. The auto-cancel rule in Shopify Flow is the enforcement; this is the
  part that keeps honest customers out of a dead end.
*/

(function () {
  const config = window.orderLimits || {};
  const maxPerItem = parseInt(config.maxPerItem, 10) || 0;
  const maxPerOrder = parseInt(config.maxPerOrder, 10) || 0;

  if (maxPerItem <= 0 && maxPerOrder <= 0) return;

  const CHECKOUT_SELECTOR = '#checkout, #CartDrawer-Checkout, .cart__checkout-button';
  const CART_FORM_SELECTOR = '#cart, #CartDrawer-Form, form[action$="/cart"]';

  /* Returns the message for the first cap the cart breaches, or '' if it is fine. */
  function breachMessage(cart) {
    if (!cart || !Array.isArray(cart.items)) return '';

    if (maxPerItem > 0) {
      const offender = cart.items.find((item) => item.quantity > maxPerItem);
      if (offender) {
        const name = offender.product_title || offender.title || 'this item';
        return `You can order up to ${maxPerItem} of ${name} in one go. Please lower the quantity to carry on.`;
      }
    }

    if (maxPerOrder > 0 && cart.item_count > maxPerOrder) {
      return `One order can hold up to ${maxPerOrder} items in total. Your cart has ${cart.item_count}. Please lower the quantity to carry on.`;
    }

    return '';
  }

  function render(message) {
    document.querySelectorAll('[data-cart-limit-notice]').forEach((notice) => {
      const target = notice.querySelector('[data-cart-limit-message]');
      if (target) target.textContent = message;
      notice.hidden = message === '';
    });

    document.querySelectorAll(CHECKOUT_SELECTOR).forEach((button) => {
      if (message) {
        button.setAttribute('disabled', 'disabled');
        button.dataset.cartLimitBlocked = 'true';
      } else if (button.dataset.cartLimitBlocked === 'true') {
        /* Only re-enable what this file disabled. An empty cart disables the
           same button through Liquid and must stay disabled. */
        button.removeAttribute('disabled');
        delete button.dataset.cartLimitBlocked;
      }
    });

    /* Wallet buttons post straight to checkout and ignore the button above. */
    document.querySelectorAll('.cart__dynamic-checkout-buttons').forEach((wrapper) => {
      wrapper.hidden = message !== '';
    });
  }

  let lastMessage = '';

  function check() {
    return fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)
      .then((response) => response.json())
      .then((cart) => {
        lastMessage = breachMessage(cart);
        render(lastMessage);
        return lastMessage;
      })
      .catch(() => '');
  }

  /* Last line of defence. Someone can land on the cart already over the cap by
     following a /cart/{variant}:{quantity} link, so the submit itself is held
     until a fresh cart read says the cart is within the caps. */
  function guardSubmit(event) {
    const form = event.target.closest(CART_FORM_SELECTOR);
    if (!form) return;

    if (lastMessage) {
      event.preventDefault();
      render(lastMessage);
      const notice = document.querySelector('[data-cart-limit-notice]:not([hidden])');
      if (notice) notice.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  document.addEventListener('submit', guardSubmit, true);

  /* PUB_SUB_EVENTS.cartUpdate fires for the drawer, the cart page and quick add. */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, check);
  }

  document.addEventListener('change', (event) => {
    if (event.target.classList && event.target.classList.contains('quantity__input')) {
      /* The cart PATCH lands a moment after the input settles. */
      setTimeout(check, 700);
    }
  });

  check();
})();
